import mapboxgl from 'mapbox-gl'
import { getDistanceBetween } from 'geolocation-distance-between'
import '@/Webstomp'
import partition from '@/utils/partition'

// mesured in KM
const MAX_DISTANCE_BETWEEN_GROUPED_STOPS = 1 / 1000 // 1 Meter

const flatten = (list) => list.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), [])

function createMap() {
    mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN
    const mapView = document.getElementById('map-view')
    const map = new mapboxgl.Map({
        container: 'map-view',
        style: 'mapbox://styles/budbee/cjv2amfmz08aa1fnunbivtssk',
        center: [18.06324, 59.334591],
        zoom: 12,
    })

    return {
        map,
        mapView,
    }
}

function stopTypeIcon(stopType) {
    if (stopType === 'TERMINAL') {
        return 'warehouse'
    }
    if (stopType === 'CROSSDOCK') {
        return 'truck'
    }
    return ''
}

function groupStops(stops) {
    const defaultGroupedStops = stops.slice().map((stop) => [stop])

    return defaultGroupedStops.reduce((groupedStops, stop) => {
        const nextStop = groupedStops.find((groupedStop) => groupedStop[0] === stop[0])

        if (!nextStop) {
            return groupedStops
        }

        const [conflictingStops, rest] = partition(
            groupedStops,
            (other) =>
                other.length === 1 &&
                other[0].sequence !== stop[0].sequence &&
                getDistanceBetween(other[0], stop[0]) < MAX_DISTANCE_BETWEEN_GROUPED_STOPS,
        )

        const index = rest.findIndex((s) => s[0] === stop[0])

        rest[index] = [...stop, ...flatten(conflictingStops)]

        return rest
    }, defaultGroupedStops)
}

function addStopMarkersToMap(map, stopGroups, onClick) {
    return stopGroups.map((items) => {
        const markerHtml = document.createElement('div')
        markerHtml.className = 'marker'

        const stopMarkers = items.map((item, itemIndex) => {
            const iconAndSequence = document.createElement('div')
            iconAndSequence.className = 'icon-and-sequence'

            const isLastItem = itemIndex + 1 === items.length
            if (isLastItem) {
                iconAndSequence.className += ' icon-and-sequence--last'
            }

            iconAndSequence.addEventListener('click', onClick(item))

            const sequence = document.createElement('span')
            sequence.className = 'marker-sequence'
            sequence.innerText = item.sequence
            iconAndSequence.appendChild(sequence)

            const iconName = stopTypeIcon(item.type)
            if (iconName) {
                const icon = document.createElement('i')
                icon.className = `fas fa-${stopTypeIcon(item.type)} marker-icon`
                iconAndSequence.appendChild(icon)
            }

            return iconAndSequence
        })

        const markerSpine = document.createElement('div')
        markerSpine.className = 'marker-spine'

        const markerDot = document.createElement('div')
        markerDot.className = 'marker-dot'

        const markerContent = document.createElement('div')
        markerContent.className = 'marker-content'

        stopMarkers.forEach((stopMarker) => markerContent.appendChild(stopMarker))
        markerHtml.appendChild(markerContent)
        markerHtml.appendChild(markerSpine)
        markerHtml.appendChild(markerDot)

        const marker = new mapboxgl.Marker(markerHtml, { offset: [0, 0], anchor: 'bottom' })
            .setLngLat([items[0].longitude, items[0].latitude])
            .addTo(map)

        return {
            marker,
            element: markerHtml,
            items,
            spine: markerSpine,
        }
    })
}

function stopSequenceSorter(stopA, stopB) {
    return stopB.sequence - stopA.sequence
}

function stopToCoordinates(stop) {
    return [stop.longitude, stop.latitude]
}

function drawLinesBetweenStops(map, stops) {
    map.addLayer({
        id: 'route',
        type: 'line',
        source: {
            type: 'geojson',
            data: {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: stops.sort(stopSequenceSorter).map(stopToCoordinates),
                },
            },
        },
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
        },
        paint: {
            'line-color': '#3A5FA9',
            'line-width': 6,
        },
    })
}

function addDriverMarkerToMap(map, { longitude, latitude }) {
    const markerHtml = document.createElement('div')
    markerHtml.className = 'driver-marker'

    return new mapboxgl.Marker(markerHtml, { offset: [0, 0] })
        .setLngLat([longitude, latitude])
        .addTo(map)
}

function moveDriverOnMap(driverMarker, { longitude, latitude }) {
    driverMarker.setLngLat([longitude, latitude])

    return driverMarker
}

function updateDriverOnMap(map, driverMarker, driverCoordinate) {
    if (!driverMarker) {
        return addDriverMarkerToMap(map, driverCoordinate)
    }

    return moveDriverOnMap(driverMarker, driverCoordinate)
}

function centerMapOnCoordinates(map, coordinates, options = {}) {
    const mapBounds = coordinates.reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
    )

    map.fitBounds(mapBounds, {
        padding: 80,
        linear: true,
        maxZoom: 16,
        ...options,
    })
}

function centerMapOnStops(map, markers, options) {
    const coordinates = markers.map((marker) => marker.marker.getLngLat())

    centerMapOnCoordinates(map, coordinates, options)
}

export default {
    name: 'RouteMap',
    props: {
        data: Array,
        driverCoordinate: Object,
        selectedStopMarker: Object,
        onMarkerClick: Function,
    },
    data: () => ({
        map: null,
        mapView: null,
        stopMarkers: [],
        driverMarker: null,
    }),
    watch: {
        driverCoordinate(newVal) {
            this.$data.driverMarker = updateDriverOnMap(
                this.$data.map,
                this.$data.driverMarker,
                newVal,
            )
        },
        selectedStopMarker(newVal) {
            if (newVal === null) {
                centerMapOnStops(this.map, this.stopMarkers)
            } else {
                centerMapOnCoordinates(this.map, [[newVal.longitude, newVal.latitude]])
            }
        },
    },
    mounted() {
        const { map, mapView } = createMap()

        this.$data.map = map
        this.$data.mapView = mapView

        const markers = groupStops(this.$props.data)
        const stopMarkers = addStopMarkersToMap(map, markers, this.$props.onMarkerClick)

        this.$data.stopMarkers = stopMarkers

        centerMapOnStops(map, stopMarkers)

        map.on('load', () => {
            drawLinesBetweenStops(map, this.$props.data)
            if (this.$props.driverCoordinate) {
                this.$data.driverMarker = updateDriverOnMap(
                    this.$data.map,
                    this.$data.driverMarker,
                    this.$props.driverCoordinate,
                )
            }
        })
    },
}
